/**
 * Presentation helpers shared by the per-peer link UI (badge, overlay, header,
 * details): status colours, grade letters, number formatting and the mapping
 * from (machine state, transport state, media health) to a dot status.
 *
 * Helpers that produce user-visible text take the i18n `t` of the caller
 * (react-i18next useTranslation); without one they return the key paths.
 */
import { PEER_STATES, RECONNECT_DEFAULTS } from "features/robot-teleop/webrtc/peerStates";

const keyOnly = (key) => key;

export const STATUS_COLORS = {
  good: "#0ca30c",
  warning: "#fab219",
  serious: "#ec835a",
  critical: "#d03b3b",
  neutral: "#898781",
};

export const GRADE_LETTER = { good: "A", warning: "B", serious: "C", critical: "D" };

export const gradeColor = (grade) => STATUS_COLORS[grade] || STATUS_COLORS.neutral;

/** Translated word for a grade ("good" / "fair" / "poor" / "bad"). */
export const gradeText = (grade, t = keyOnly) => (grade ? t(`teleop.peer.quality.${grade}`) : "");

export const fmtMs = (v, digits = 0) => (v == null || !Number.isFinite(v) ? "–" : `${v.toFixed(digits)}`);
export const fmtNum = (v, digits = 0) => (v == null || !Number.isFinite(v) ? "–" : v.toFixed(digits));
export const fmtKbps = (v) => {
  if (v == null || !Number.isFinite(v)) return "–";
  return v >= 1000 ? `${(v / 1000).toFixed(1)} Mb/s` : `${Math.round(v)} kb/s`;
};
export const fmtPct = (v) => (v == null || !Number.isFinite(v) ? "–" : `${v.toFixed(1)}%`);
export const fmtPath = (path, t = keyOnly) => {
  if (!path) return "–";
  if (path.relayed) return t("teleop.peer.details.pathRelay");
  const kind = path.local === "host" && path.remote === "host"
    ? t("teleop.peer.details.pathDirectLan")
    : t("teleop.peer.details.pathDirect");
  return `${kind}${path.protocol ? ` · ${path.protocol}` : ""}`;
};

export const MEDIA_HEALTH_KEY = {
  stalled: "teleop.peer.state.noVideo",
  "no-track": "teleop.peer.state.noTrack",
};

/**
 * Dot status for a peer tile.
 * @returns {{ cls: string, text: string, alert: boolean }}
 */
export const dotStatus = ({ state, connectionState, health, grade, retryCount, expectsVideo }, t = keyOnly) => {
  switch (state) {
    case PEER_STATES.CONNECTED: {
      if (expectsVideo && (health === "stalled" || health === "no-track")) {
        return { cls: "stalled", text: t(MEDIA_HEALTH_KEY[health]), alert: true };
      }
      if (connectionState && connectionState !== "connected") {
        return {
          cls: "degraded",
          text: connectionState === "disconnected" ? t("teleop.peer.state.disconnected") : connectionState,
          alert: true,
        };
      }
      if (grade === "serious" || grade === "critical") {
        return { cls: "degraded", text: t("teleop.peer.state.connected"), alert: false };
      }
      return { cls: "ok", text: t("teleop.peer.state.connected"), alert: false };
    }
    case PEER_STATES.CONNECTING:
      return { cls: "connecting", text: t("teleop.peer.state.connecting"), alert: false };
    case PEER_STATES.RECONNECTING:
      return {
        cls: "connecting",
        text: t("teleop.peer.state.reconnecting", { n: retryCount || 0, max: RECONNECT_DEFAULTS.maxRetries }),
        alert: false,
      };
    case PEER_STATES.FAILED:
      return { cls: "failed", text: t("teleop.peer.state.failed"), alert: true };
    case PEER_STATES.CLOSED:
      return { cls: "idle", text: t("teleop.peer.state.closed"), alert: false };
    default:
      return { cls: "idle", text: t("teleop.peer.state.idle"), alert: false };
  }
};

/** Human-readable cause for a stalled / missing video, from the last sample. */
export const stallCause = (health, sample, t = keyOnly) => {
  if (health === "no-track") return t("teleop.peer.stall.cause.noTrack");
  const v = sample?.video;
  if (!v) return t("teleop.peer.stall.cause.noStats");
  if (v.bytesReceivedDelta != null && v.bytesReceivedDelta > 0) return t("teleop.peer.stall.cause.noDecode");
  if (v.lastPacketReceivedTimestamp != null && sample.timestamp != null) {
    const ageS = Math.max(0, (sample.timestamp - v.lastPacketReceivedTimestamp) / 1000);
    if (ageS >= 1) return t("teleop.peer.stall.cause.noPacketsFor", { n: Math.round(ageS) });
  }
  return t("teleop.peer.stall.cause.noPackets");
};
